
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Dialog, { DialogType } from '../components/ui/Dialog';
import { api, Store, Staff, getTrialStatus } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

// Import Tabs
import StaffSettingsTab from '../components/settings/StaffSettingsTab';
import PositionSettingsTab from '../components/settings/PositionSettingsTab';
import DutySettingsTab from '../components/settings/DutySettingsTab';
import StaffRuleSettingsTab from '../components/settings/StaffRuleSettingsTab';
import ShiftTypeSettingsTab from '../components/settings/ShiftPatternSettingsTab';
import ShiftPatternSettingsTab from '../components/settings/ShiftTemplateSettingsTab';
import CsvSettingsTab from '../components/settings/CsvSettingsTab';
import GeneralSettingsTab from '../components/settings/GeneralSettingsTab';

const SettingsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'staff');
  const [stores, setStores] = useState<Store[]>([]);
  const [myProfile, setMyProfile] = useState<Staff | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dialog, setDialog] = useState<{isOpen: boolean, type: DialogType, title: string, message: string, onConfirm: (val?: string) => void}>({
      isOpen: false, type: 'alert', title: '', message: '', onConfirm: () => {}
  });

  useEffect(() => {
      if (!currentUser) return;
      const fetchData = async () => {
          setIsLoading(true);
          try {
              const [loadedStores, loadedStaff] = await Promise.all([
                  api.getStores(), api.getStaff()
              ]);
              setStores(loadedStores || []);
              setMyProfile(loadedStaff.find(s => s.id === currentUser.uid) || null);
          } catch (e) { 
              console.error(e); 
              showToast('データの取得に失敗しました。', 'error');
          } finally { 
              setIsLoading(false); 
          }
      };
      fetchData();
  }, [currentUser]);

  // 勤怠関連機能（CSV設定等）が表示されるかどうかの判定
  const isAttendanceVisible = useMemo(() => {
    if (stores.length === 0) return false;
    const store = stores[0];
    const trial = getTrialStatus(store);
    const isTrialActive = !store.stripeSubscriptionId && !trial.isExpired;
    return store.plan === 'business' || isTrialActive;
  }, [stores]);

  const changeTab = (tab: string) => {
      setActiveTab(tab);
      setSearchParams({ tab });
  };

  const isSystemAdmin = myProfile?.role === 'system_admin';

  const tabClass = (id: string) => `px-6 py-3 font-bold text-sm border-b-2 whitespace-nowrap transition-colors rounded-none ${activeTab === id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`;

  return (
    <div className="animate-in">
        <Dialog isOpen={dialog.isOpen} type={dialog.type} title={dialog.title} message={dialog.message} onConfirm={dialog.onConfirm} onCancel={() => setDialog(prev => ({...prev, isOpen: false}))} />
        
        <div className="space-y-8 pb-20 relative">
            <div className="flex border-b border-gray-200 overflow-x-auto no-scrollbar">
                <button onClick={() => changeTab('staff')} className={tabClass('staff')}>スタッフ管理</button>
                <button onClick={() => changeTab('positions')} className={tabClass('positions')}>ポジション設定</button>
                <button onClick={() => changeTab('duties')} className={tabClass('duties')}>対応業務設定</button>
                <button onClick={() => changeTab('rules')} className={tabClass('rules')}>ペア・相性NG</button>
                <button onClick={() => changeTab('shift-types')} className={tabClass('shift-types')}>シフト設定</button>
                <button onClick={() => changeTab('shift-patterns')} className={tabClass('shift-patterns')}>モデルシフト設定</button>
                {/* 勤怠管理が有効な場合のみCSV設定を表示 */}
                {isAttendanceVisible && (
                    <button onClick={() => changeTab('csv')} className={tabClass('csv')}>CSV設定</button>
                )}
                <button onClick={() => changeTab('general')} className={tabClass('general')}>企業・店舗情報</button>
            </div>

            <div className="min-h-[400px]">
                {activeTab === 'staff' && (
                    <StaffSettingsTab stores={stores} myProfile={myProfile} isSystemAdmin={isSystemAdmin} />
                )}
                {activeTab === 'positions' && (
                    <PositionSettingsTab stores={stores} myProfile={myProfile} isSystemAdmin={isSystemAdmin} />
                )}
                {activeTab === 'rules' && (
                    <StaffRuleSettingsTab stores={stores} myProfile={myProfile} isSystemAdmin={isSystemAdmin} />
                )}
                {activeTab === 'duties' && (
                    <DutySettingsTab stores={stores} myProfile={myProfile} isSystemAdmin={isSystemAdmin} />
                )}
                {activeTab === 'shift-types' && (
                    <ShiftTypeSettingsTab stores={stores} myProfile={myProfile} isSystemAdmin={isSystemAdmin} />
                )}
                {activeTab === 'shift-patterns' && (
                    <ShiftPatternSettingsTab stores={stores} myProfile={myProfile} isSystemAdmin={isSystemAdmin} />
                )}
                {activeTab === 'csv' && isAttendanceVisible && (
                    <CsvSettingsTab stores={stores} myProfile={myProfile} isSystemAdmin={isSystemAdmin} onStoresChange={setStores} />
                )}
                {activeTab === 'general' && (
                    <GeneralSettingsTab stores={stores} myProfile={myProfile} isSystemAdmin={isSystemAdmin} currentUser={currentUser} onStoresChange={setStores} />
                )}
            </div>
        </div>
    </div>
  );
};

export default SettingsPage;
